import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Building, 
  Mail, 
  Phone, 
  MapPin,
  FileText,
  CreditCard,
  Users,
  User,
  Calendar,
  Download,
  Eye,
  AlertCircle
} from 'lucide-react';
import { getVendorById, updateVendorStatus } from '../firebaseService';
import { useAuth } from './AuthProvider';
import { useMsal } from '@azure/msal-react';
import SharePointStatus from './SharePointStatus';
import { sharePointRequest } from '../authConfig';

interface Vendor {
  id: string;
  companyName: string;
  telephone: string;
  fax: string;
  email: string;
  registeredAddress: string;
  ownersPartners: string;
  ownerAddress: string;
  registrationNo: string;
  registrationDate: string;
  tinNo: string;
  isLimitedLiability: string;
  paidUpCapital: string;
  lastTurnover: string;
  isISO9001: string;
  categories: Record<string, boolean>;
  specializationDetails: string;
  extendCredit: string;
  creditVolume: string;
  creditTenure: string;
  paymentPlan: string;
  postDeliveryPayment: string;
  bankingInfo: Array<{
    bankName: string;
    accountType: string;
    accountNumber: string;
  }>;
  bvn?: string;
  references: Array<{
    company: string;
    telNo: string;
    date: string;
    location: string;
    contractValue: string;
    clientContact: string;
  }>;
  status: 'pending' | 'approved' | 'rejected';
  submittedAt: any;
  documentUrls?: Record<string, string>;
  notes?: string;
  approvedBy?: string;
  reviewedAt?: any;
  
  // Contact Person Details
  contactPersonName?: string;
  contactPersonPhone?: string;
  contactPersonDesignation?: string;
  declarationAccepted?: boolean;
}

const VendorDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { instance } = useMsal();

  // Debug configuration
  useEffect(() => {
    console.log('SharePoint Configuration Check:');
    console.log('REACT_APP_SHAREPOINT_SITE_URL:', process.env.REACT_APP_SHAREPOINT_SITE_URL);
    console.log('REACT_APP_SHAREPOINT_LIST_NAME:', process.env.REACT_APP_SHAREPOINT_LIST_NAME);
    console.log('REACT_APP_SHAREPOINT_DOC_LIBRARY:', process.env.REACT_APP_SHAREPOINT_DOC_LIBRARY);
    console.log('REACT_APP_AZURE_CLIENT_ID:', process.env.REACT_APP_AZURE_CLIENT_ID);
    console.log('REACT_APP_AZURE_TENANT_ID:', process.env.REACT_APP_AZURE_TENANT_ID);
    console.log('User authenticated:', !!user);
    console.log('MSAL instance present:', !!instance);
    
    if (instance) {
      console.log('Active account:', instance.getActiveAccount());
      console.log('All accounts:', instance.getAllAccounts());
    }
    
    // Show SharePoint scope that will be used
    const sharePointScope = `${process.env.REACT_APP_SHAREPOINT_SITE_URL}/.default`;
    console.log('SharePoint scope for token:', sharePointScope);
  }, [user, instance]);
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [documentsConfirmed, setDocumentsConfirmed] = useState(false);
  const [action, setAction] = useState<'approve' | 'reject' | null>(null);

  useEffect(() => {
    if (id) {
      fetchVendor();
    }
  }, [id]);

  const fetchVendor = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const result = await getVendorById(id);
      if (result.success && result.vendor) {
        setVendor(result.vendor as Vendor);
        console.log('Vendor data fetched:', JSON.stringify(result.vendor, null, 2));
        setNotes((result.vendor as any).notes || '');
      } else {
        console.error('Error fetching vendor:', result.error);
      }
    } catch (error) {
      console.error('Error fetching vendor:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (status: 'approved' | 'rejected') => {
    if (!id) return;
    
    setActionLoading(true);
    try {
      // Get access token for SharePoint
      let accessToken = '';
      try {
        // Try to get active account first
        let account = instance.getActiveAccount();
        console.log('Active account from getActiveAccount():', account);
        
        // If no active account, try to get from accounts array
        if (!account) {
          const accounts = instance.getAllAccounts();
          console.log('All accounts:', accounts);
          if (accounts.length > 0) {
            account = accounts[0];
            console.log('Using first account from accounts array:', account);
          }
        }
        
        if (account) {
          // Use Microsoft Graph scopes for SharePoint access
          const graphScopes = [
            'Sites.ReadWrite.All',
            'Files.ReadWrite.All',
            'Sites.FullControl.All'
          ];
          const tokenRequest = {
            scopes: graphScopes,
            account: account,
          };
          console.log('Requesting Microsoft Graph token with scopes:', tokenRequest.scopes);
          const tokenResponse = await instance.acquireTokenSilent(tokenRequest);
          accessToken = tokenResponse.accessToken;
          console.log('Microsoft Graph access token acquired successfully');
        } else {
          console.warn('No account found for token acquisition');
        }
      } catch (tokenError: any) {
        console.error('Could not get access token for SharePoint:', tokenError);
        console.error('Token error details:', {
          errorCode: tokenError?.errorCode,
          errorMessage: tokenError?.errorMessage,
          subError: tokenError?.subError
        });
        
        // If silent token acquisition fails, try interactive
        try {
          console.log('Attempting interactive Microsoft Graph token acquisition...');
          const graphScopes = [
            'Sites.ReadWrite.All',
            'Files.ReadWrite.All',
            'Sites.FullControl.All'
          ];
          const interactiveTokenRequest = {
            scopes: graphScopes,
          };
          const interactiveResponse = await instance.acquireTokenPopup(interactiveTokenRequest);
          accessToken = interactiveResponse.accessToken;
          console.log('Interactive Microsoft Graph token acquisition successful');
        } catch (interactiveError: any) {
          console.error('Interactive Microsoft Graph token acquisition also failed:', interactiveError);
        }
      }

      const approvedBy = user?.name || user?.username || 'Unknown User';
      const result = await updateVendorStatus(id, status, notes, approvedBy, accessToken);
      
      if (result.success) {
        setVendor(prev => prev ? { ...prev, status, notes } : null);
        setAction(null);
        setShowNotes(false);
        setNotes('');
        setDocumentsConfirmed(false);
        
        // Show success message with SharePoint info
        if (status === 'approved' && accessToken) {
          alert('Vendor approved successfully and synced to SharePoint!');
        } else if (status === 'approved') {
          alert('Vendor approved successfully! (SharePoint sync unavailable - check Azure AD configuration and .env file)');
        } else {
          alert('Vendor status updated successfully!');
        }
      } else {
        console.error('Error updating status:', result.error);
        alert('Error updating vendor status: ' + result.error);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating vendor status: ' + error);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const getCategoryName = (category: string) => {
    const documentNames: Record<string, string> = {
      applicationLetter: 'Application Letter',
      incorporationCert: 'Certificate of Incorporation',
      vatCert: 'VAT Certificate',
      passportPhoto: 'Passport Photograph',
      previousJobEvidence: 'Previous Job Evidence',
      taxClearance: 'Tax Clearance Certificate',
      performanceBond: 'Performance Bond',
      referenceLetter: 'Reference Letters',
      contactPersonSignature: 'Contact Person Signature',
      memat: 'Memorandum and Article of Association (MEMAT)',
      identityDocuments: 'Identity Documents of Principal Owners',
      utilityBill: 'Utility Bill or Proof of Business Address',
      cacStatusReport: 'CAC Status Report',
      // New optional document labels
      letterOfIntroduction: 'Letter of Introduction / Intent to Engage',
      boardResolution: 'Board Resolution Authorizing Contract Execution',
      companyProfile: 'Company Profile / Capability Statement',
      taxComplianceCertificate: 'Tax Compliance Certificate (TCC)',
      exportImportLicense: 'Export/Import License',
      cacForm2And7: 'CAC Form 2 & Form 7 (Particulars of Shareholders and Directors)',
      iso9001Certificate: 'ISO 9001 or other Quality Management certifications',
      productComplianceCertificates: 'Product Compliance Certificates (CE/UL/etc.)',
      hsePolicy: 'Health, Safety & Environmental (HSE) Policy',
      safetyRecords: 'Safety Records or Incident History',
      generalLiabilityInsurance: 'General Liability Insurance Certificate',
      productLiabilityInsurance: 'Product Liability or Professional Indemnity Insurance',
      supplierCodeOfConduct: 'Signed Supplier Code of Conduct',
      antiBriberyPolicyAcknowledgment: 'Anti-Bribery and Corruption Policy Acknowledgment',
      dataProtectionPolicy: 'Data Protection or Ethics Compliance Policy'
    };
    
    return documentNames[category] || category.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sunbeth-orange-600" />
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <AlertCircle className="h-12 w-12 text-red-400" />
        <h3 className="mt-4 text-sm font-medium text-gray-900">Vendor not found</h3>
        <p className="mt-1 text-sm text-gray-500">The vendor you're looking for doesn't exist.</p>
        <button
          onClick={() => navigate('/staff/dashboard/vendors')}
          className="mt-4 bg-sunbeth-orange-600 text-white px-4 py-2 rounded-lg hover:bg-sunbeth-orange-700"
        >
          Back to Vendors
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 sm:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-start gap-3">
              <button
                onClick={() => navigate('/staff/dashboard/vendors')}
                className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{vendor.companyName}</h2>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(vendor.status)}`}>
                    {getStatusIcon(vendor.status)}
                    <span className="ml-1 capitalize">{vendor.status}</span>
                  </span>
                  <span className="text-sm text-gray-500">
                    Submitted: {formatDate(vendor.submittedAt)}
                  </span>
                  <SharePointStatus 
                      vendorId={vendor.id}
                      status={vendor.status}
                      approvedBy={vendor.approvedBy}
                      approvedDate={vendor.reviewedAt}
                    />
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 flex-shrink-0">
              {vendor.status === 'pending' && (
                <div className="flex gap-3">
                  <button
                    onClick={() => setAction('reject')}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </button>
                  <button
                    onClick={() => setAction('approve')}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </button>
                </div>
              )}
            </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Business Details */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Building className="w-5 h-5 mr-2" />
                  Business Details
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 text-left">Company Name</label>
                    <p className="mt-1 text-sm text-gray-900 text-left">{vendor.companyName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 text-left">Email</label>
                    <p className="mt-1 text-sm text-gray-900 flex items-center text-left">
                      <Mail className="w-4 h-4 mr-1" />
                      {vendor.email}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 text-left">Telephone</label>
                    <p className="mt-1 text-sm text-gray-900 flex items-center text-left">
                      <Phone className="w-4 h-4 mr-1" />
                      {vendor.telephone}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 text-left">Fax</label>
                    <p className="mt-1 text-sm text-gray-900 text-left">{vendor.fax || 'N/A'}</p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 text-left">Registered Address</label>
                  <p className="mt-1 text-sm text-gray-900 flex items-start text-left">
                    <MapPin className="w-4 h-4 mr-1 mt-0.5" />
                    {vendor.registeredAddress}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 text-left">Registration Number</label>
                    <p className="mt-1 text-sm text-gray-900 text-left">{vendor.registrationNo || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 text-left">Registration Date</label>
                    <p className="mt-1 text-sm text-gray-900 text-left">{vendor.registrationDate || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 text-left">TIN Number</label>
                    <p className="mt-1 text-sm text-gray-900 text-left">{vendor.tinNo || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 text-left">Paid Up Capital</label>
                    <p className="mt-1 text-sm text-gray-900 text-left">{vendor.paidUpCapital || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Categories & Services */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Categories & Services
                </h3>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service Categories</label>
                  <div className="flex flex-wrap gap-2">
                    {vendor.categories && Object.entries(vendor.categories)
                      .filter(([_, selected]) => selected)
                      .map(([category, _]) => (
                        <span key={category} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-sunbeth-orange-100 text-sunbeth-orange-800">
                          {getCategoryName(category)}
                        </span>
                      ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Specialization Details</label>
                  <p className="mt-1 text-sm text-gray-900">{vendor.specializationDetails || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Credit Information */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <CreditCard className="w-5 h-5 mr-2" />
                  Credit Information
                </h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Extend Credit Facilities</label>
                    <p className="mt-1 text-sm text-gray-900 capitalize">{vendor.extendCredit || 'N/A'}</p>
                  </div>
                  
                  {vendor.extendCredit === 'yes' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Credit Volume</label>
                        <p className="mt-1 text-sm text-gray-900">{vendor.creditVolume || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Credit Tenure</label>
                        <p className="mt-1 text-sm text-gray-900">{vendor.creditTenure || 'N/A'}</p>
                      </div>
                    </>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Payment Plan</label>
                    <p className="mt-1 text-sm text-gray-900">{vendor.paymentPlan || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Post Delivery Payment</label>
                    <p className="mt-1 text-sm text-gray-900">{vendor.postDeliveryPayment || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Person Information */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Contact Person Information
                </h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Person Name</label>
                    <p className="mt-1 text-sm text-gray-900">{vendor.contactPersonName || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                    <p className="mt-1 text-sm text-gray-900">{vendor.contactPersonPhone || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Designation</label>
                    <p className="mt-1 text-sm text-gray-900">{vendor.contactPersonDesignation || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Declaration Accepted</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {vendor.declarationAccepted ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          No
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Banking Information */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <CreditCard className="w-5 h-5 mr-2" />
                  Banking Information
                </h3>
              </div>
              <div className="p-6">
                {vendor.bankingInfo && vendor.bankingInfo.length > 0 ? (
                  <div className="space-y-4">
                    {vendor.bankingInfo.map((bank, index) => (
                      <div key={index} className="border rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Bank Name</label>
                            <p className="mt-1 text-sm text-gray-900">{bank.bankName}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Account Type</label>
                            <p className="mt-1 text-sm text-gray-900">{bank.accountType}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Account Number</label>
                            <p className="mt-1 text-sm text-gray-900">{bank.accountNumber}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No banking information provided</p>
                )}
                
                {/* BVN Field */}
                {vendor.bvn && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Bank Verification Number (BVN)</label>
                        <p className="mt-1 text-sm text-gray-900 font-mono">{vendor.bvn}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* References */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  Client References
                </h3>
              </div>
              <div className="p-6">
                {vendor.references && vendor.references.length > 0 ? (
                  <div className="space-y-4">
                    {vendor.references.map((ref, index) => (
                      <div key={index} className="border rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Company</label>
                            <p className="mt-1 text-sm text-gray-900">{ref.company}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Phone</label>
                            <p className="mt-1 text-sm text-gray-900">{ref.telNo}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Date</label>
                            <p className="mt-1 text-sm text-gray-900">{ref.date}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Location</label>
                            <p className="mt-1 text-sm text-gray-900">{ref.location}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Contract Value</label>
                            <p className="mt-1 text-sm text-gray-900">{ref.contractValue}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Client Contact</label>
                            <p className="mt-1 text-sm text-gray-900">{ref.clientContact}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No references provided</p>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Documents */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Documents</h3>
              </div>
              <div className="p-6">
                {vendor.documentUrls && Object.keys(vendor.documentUrls).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(vendor.documentUrls).map(([docType, url]) => (
                      <div key={docType} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center">
                          <FileText className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900">
                            {getCategoryName(docType)}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sunbeth-orange-600 hover:text-sunbeth-orange-800"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                          <a
                            href={url}
                            download
                            className="text-green-600 hover:text-green-800"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No documents uploaded</p>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Notes</h3>
              </div>
              <div className="p-6">
                {vendor.notes ? (
                  <p className="text-sm text-gray-900">{vendor.notes}</p>
                ) : (
                  <p className="text-sm text-gray-500">No notes added</p>
                )}
              </div>
            </div>
          </div>
        </div>

      {/* Action Modal */}
      {action && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {action === 'approve' ? 'Approve Vendor' : 'Reject Vendor'}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recommendation (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Add any recommendation about this decision..."
              />
            </div>

            <div className="mb-4">
              <label className="flex items-center text-left">
                <input
                  type="checkbox"
                  checked={documentsConfirmed}
                  onChange={(e) => setDocumentsConfirmed(e.target.checked)}
                  className="mr-2 h-4 w-4 text-sunbeth-orange-600 focus:ring-sunbeth-orange-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700 text-left">
                  I confirm that I have read and verified that all required documents are submitted and valid
                </span>
              </label>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setAction(null);
                  setNotes('');
                  setDocumentsConfirmed(false);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => handleStatusUpdate(action === 'approve' ? 'approved' : 'rejected')}
                disabled={actionLoading || !documentsConfirmed}
                className={`px-4 py-2 text-white rounded-lg flex items-center ${
                  action === 'approve' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                } ${actionLoading || !documentsConfirmed ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {actionLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    {action === 'approve' ? <CheckCircle className="w-4 h-4 mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                    {action === 'approve' ? 'Approve' : 'Reject'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorDetail;
